// Environment Configuration Types and Validation

export type AppEnvironment = 'development' | 'staging' | 'production'
export type LogLevel = 'debug' | 'info' | 'warn' | 'error'

export interface EnvironmentConfig {
  // Environment
  APP_ENV: AppEnvironment
  DEBUG_MODE: boolean
  LOG_LEVEL: LogLevel

  // API Configuration
  API_BASE_URL: string
  API_VERSION: string
  API_KEY: string

  // AWS Cognito
  COGNITO_REGION: string
  COGNITO_USER_POOL_ID: string
  COGNITO_CLIENT_ID: string

  // Stripe
  STRIPE_PUBLISHABLE_KEY: string

  // Application URLs
  APP_URL: string
  SUCCESS_URL: string
  CANCEL_URL: string
}

// Validation functions
function validateUrl(url: string, name: string): string {
  try {
    new URL(url)
    return url
  } catch {
    throw new Error(`Invalid URL for ${name}: ${url}`)
  }
}

function validateRequired(value: string | undefined, name: string): string {
  if (!value || value.trim() === '') {
    throw new Error(`Missing required environment variable: ${name}`)
  }
  return value.trim()
}

function validateEnvironment(env: string | undefined): AppEnvironment {
  const validEnvs: AppEnvironment[] = ['development', 'staging', 'production']
  if (!env || !validEnvs.includes(env as AppEnvironment)) {
    console.warn(`Invalid APP_ENV: ${env}, defaulting to development`)
    return 'development'
  }
  return env as AppEnvironment
}

function validateLogLevel(level: string | undefined): LogLevel {
  const validLevels: LogLevel[] = ['debug', 'info', 'warn', 'error']
  if (!level || !validLevels.includes(level as LogLevel)) {
    return 'info' // Default log level
  }
  return level as LogLevel
}

function validateBoolean(value: string | undefined, defaultValue: boolean = false): boolean {
  if (!value) return defaultValue
  return value.toLowerCase() === 'true'
}

// Load and validate environment configuration
function loadEnvironmentConfig(): EnvironmentConfig {
  try {
    const config: EnvironmentConfig = {
      // Environment
      APP_ENV: validateEnvironment(import.meta.env.VITE_APP_ENV),
      DEBUG_MODE: validateBoolean(import.meta.env.VITE_DEBUG_MODE),
      LOG_LEVEL: validateLogLevel(import.meta.env.VITE_LOG_LEVEL),

      // API Configuration
      API_BASE_URL: validateUrl(
        validateRequired(import.meta.env.VITE_API_BASE_URL, 'VITE_API_BASE_URL'),
        'API_BASE_URL'
      ),
      API_VERSION: validateRequired(import.meta.env.VITE_API_VERSION, 'VITE_API_VERSION'),
      API_KEY: validateRequired(import.meta.env.VITE_API_KEY, 'VITE_API_KEY'),

      // AWS Cognito
      COGNITO_REGION: validateRequired(import.meta.env.VITE_COGNITO_REGION, 'VITE_COGNITO_REGION'),
      COGNITO_USER_POOL_ID: validateRequired(
        import.meta.env.VITE_COGNITO_USER_POOL_ID,
        'VITE_COGNITO_USER_POOL_ID'
      ),
      COGNITO_CLIENT_ID: validateRequired(
        import.meta.env.VITE_COGNITO_CLIENT_ID,
        'VITE_COGNITO_CLIENT_ID'
      ),

      // Stripe
      STRIPE_PUBLISHABLE_KEY: validateRequired(
        import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY,
        'VITE_STRIPE_PUBLISHABLE_KEY'
      ),

      // Application URLs
      APP_URL: validateUrl(
        validateRequired(import.meta.env.VITE_APP_URL, 'VITE_APP_URL'),
        'APP_URL'
      ),
      SUCCESS_URL: validateUrl(
        validateRequired(import.meta.env.VITE_SUCCESS_URL, 'VITE_SUCCESS_URL'),
        'SUCCESS_URL'
      ),
      CANCEL_URL: validateUrl(
        validateRequired(import.meta.env.VITE_CANCEL_URL, 'VITE_CANCEL_URL'),
        'CANCEL_URL'
      ),
    }

    // Log configuration in debug mode
    if (config.DEBUG_MODE) {
      console.group('🔧 Environment Configuration')
      console.log('Environment:', config.APP_ENV)
      console.log('API Base URL:', config.API_BASE_URL)
      console.log('API Version:', config.API_VERSION)
      console.log('Debug Mode:', config.DEBUG_MODE)
      console.log('Log Level:', config.LOG_LEVEL)
      console.groupEnd()
    }

    return config
  } catch (error) {
    console.error('❌ Environment configuration error:', error)
    throw error
  }
}

// Export singleton configuration
export const env = loadEnvironmentConfig()

// Helper functions
export const isDevelopment = () => env.APP_ENV === 'development'
export const isStaging = () => env.APP_ENV === 'staging'
export const isProduction = () => env.APP_ENV === 'production'

// API URL builders
export const buildApiUrl = (endpoint: string): string => {
  const baseUrl = env.API_BASE_URL.endsWith('/') 
    ? env.API_BASE_URL.slice(0, -1) 
    : env.API_BASE_URL
  
  const version = env.API_VERSION.startsWith('/') 
    ? env.API_VERSION 
    : `/${env.API_VERSION}`
  
  const cleanEndpoint = endpoint.startsWith('/') 
    ? endpoint 
    : `/${endpoint}`
  
  return `${baseUrl}${version}${cleanEndpoint}`
}

// Logging utility
export const logger = {
  debug: (...args: any[]) => {
    if (env.DEBUG_MODE && ['debug'].includes(env.LOG_LEVEL)) {
      console.log('🐛', ...args)
    }
  },
  info: (...args: any[]) => {
    if (['debug', 'info'].includes(env.LOG_LEVEL)) {
      console.info('ℹ️', ...args)
    }
  },
  warn: (...args: any[]) => {
    if (['debug', 'info', 'warn'].includes(env.LOG_LEVEL)) {
      console.warn('⚠️', ...args)
    }
  },
  error: (...args: any[]) => {
    console.error('❌', ...args)
  }
}